import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { OverlayDrawer } from '@theme/overlay-drawer';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { fetchSection } from '@theme/section-renderer';
import { debounce, onAnimationEnd, scrollBehavior } from '@theme/utilities';

/** Section rendered while the field is empty (recently viewed products slot) */
const EMPTY_SECTION_ID = 'custom-predictive-search-empty';

/**
 * A custom element that allows the user to search for resources available on the store.
 * Lives inside the search `<overlay-drawer>` (snippets/search-modal.liquid); results come from
 * the `predictive-search` section rendered for the Predictive Search API.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * The overlay the search lives in.
   * @returns {OverlayDrawer | null}
   */
  get dialog() {
    const overlay = this.closest('overlay-drawer');
    return overlay instanceof OverlayDrawer ? overlay : null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(ThemeEvents.overlayClose, this.#handleDialogClose, { signal });
      dialog.addEventListener(ThemeEvents.overlayOpen, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      onIdle(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#activeFetch?.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      event.preventDefault();
      this.dialog?.toggle();
    }
  };

  /**
   * Handles the overlay close event.
   * @param {Event} event
   */
  #handleDialogClose = (event) => {
    if (event.target !== this.dialog) return;
    this.#resetSearch();
  };

  /**
   * @param {Event} event
   */
  #handleDialogOpen = (event) => {
    if (event.target !== this.dialog) return;
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: scrollBehavior(), block: 'nearest' });
    // The overlay hides with a transition: never pull focus back into it once it is closing
    if (!this.dialog || this.dialog.isOpen) this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      // First Escape clears the field, the next one closes the overlay (OverlayDrawer skips handled events)
      if (this.refs.searchInput.value) event.preventDefault();
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        }
        // Otherwise the form submits to the search page with its own type and prefix options
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
        this.refs.searchInput.focus();
      });
    }
  }

  /**
   * Reset the search state (`on:click="/resetSearch"` on the reset button).
   * @param {boolean | Event} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce(
    /** @param {Event} event */
    (event) => {
      // If the input is not a text input (like using the Escape key), don't search
      if (!(event instanceof InputEvent) || !event.inputType) return;

      const searchTerm = this.refs.searchInput.value.trim();
      this.#currentIndex = -1;

      if (!searchTerm.length) {
        this.#resetSearch();
        return;
      }

      this.#showResetButton();
      this.#getSearchResults(searchTerm);
    },
    200
  );

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results through the Section Rendering API and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(window.Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'query,product');
    url.searchParams.set('resources[limit]', '8');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    try {
      const resultsMarkup = await fetchSection(this.dataset.sectionId, {
        url: url.toString(),
        signal: abortController.signal,
      });
      if (abortController.signal.aborted) return;

      const results = resultsMarkup.querySelector('.predictive-search-results__content');
      if (!results) return;

      predictiveSearchResults.replaceChildren(document.importNode(results, true));
      this.#resetScrollPositions();
    } catch (error) {
      if (abortController.signal.aborted) return;
      throw error;
    }
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @param {AbortSignal} signal
   * @returns {Promise<Document | null>} The section rendered with the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup(signal) {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(window.Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');

    return fetchSection(this.dataset.sectionId, { url: url.toString(), signal });
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const { signal } = abortController;
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    try {
      const emptySectionMarkup = await fetchSection(EMPTY_SECTION_ID, { url: url.toString(), signal });
      const parsedEmptySectionMarkup = emptySectionMarkup.querySelector('.predictive-search-empty-section');

      if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

      /** This needs to be awaited and not .then so the DOM is complete before it is swapped in */
      const viewedProducts = RecentlyViewed.getProducts();

      if (viewedProducts.length > 0) {
        const parsedRecentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup(signal);
        const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup?.getElementById('predictive-search-products');
        const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');

        if (recentlyViewedProductsHtml && collectionElement) {
          for (const child of recentlyViewedProductsHtml.children) {
            if (child instanceof HTMLElement) {
              child.setAttribute('ref', 'recentlyViewedWrapper');
            }
          }

          collectionElement.prepend(...recentlyViewedProductsHtml.children);
        }
      }

      if (signal.aborted) return;

      predictiveSearchResults.replaceChildren(
        ...Array.from(parsedEmptySectionMarkup.childNodes).map((node) => document.importNode(node, true))
      );
      this.#resetScrollPositions();
    } catch (error) {
      if (signal.aborted) return;
      throw error;
    }
  };
}

/**
 * Runs the callback when the browser is idle (Safari has no requestIdleCallback).
 * @param {() => void} callback
 */
function onIdle(callback) {
  if ('requestIdleCallback' in window) window.requestIdleCallback(callback);
  else setTimeout(callback, 200);
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
